/**
 * Модальное окно. Ловушка фокуса, Escape и клик по подложке — от Headless UI,
 * своя реализация этого всегда упускает часть доступности.
 *
 * На телефоне (docs/08-mobile-roadmap.md, M2) узкое окно (`sm`, подтверждения)
 * становится нижним листом, остальные — полноэкранными: в форме на весь
 * экран поле наверху не закрывается клавиатурой, а лист с полем внизу
 * в iOS ушёл бы под неё.
 */

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { X } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "./cn";
import { IconButton } from "./IconButton";

type ModalWidth = "sm" | "md" | "lg" | "xl";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: ModalWidth;
  /** Запрещает закрытие по Escape и подложке — для операций в процессе. */
  dismissable?: boolean;
}

const WIDTHS: Record<ModalWidth, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-6xl",
};

/** Вид на телефоне: лист снизу или весь экран. */
const PHONE: Record<ModalWidth, string> = {
  sm: "max-sm:max-h-[92dvh] max-sm:rounded-b-none max-sm:border-x-0 max-sm:border-b-0",
  md: "max-sm:h-dvh max-sm:max-h-dvh max-sm:rounded-none max-sm:border-0 max-sm:pt-(--safe-top)",
  lg: "max-sm:h-dvh max-sm:max-h-dvh max-sm:rounded-none max-sm:border-0 max-sm:pt-(--safe-top)",
  xl: "max-sm:h-dvh max-sm:max-h-dvh max-sm:rounded-none max-sm:border-0 max-sm:pt-(--safe-top)",
};

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = "md",
  dismissable = true,
}: ModalProps) {
  return (
    <Dialog
      open={open}
      onClose={dismissable ? onClose : () => undefined}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/40" aria-hidden />
      <div className="fixed inset-0 flex items-center justify-center p-4 max-sm:items-end max-sm:p-0">
        <DialogPanel
          className={cn(
            "flex w-full flex-col rounded-lg border border-line bg-surface shadow-lg",
            // Высокая форма не должна вылезать за экран: скроллится тело,
            // шапка и подвал остаются видимыми.
            "max-h-[calc(100dvh-2rem)] max-sm:max-w-none",
            WIDTHS[width],
            PHONE[width],
            // Без подвала отступ под полосу «домой» берёт тело окна.
            !footer && "max-sm:pb-(--safe-bottom)",
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 px-5 pt-4 pb-3">
            <DialogTitle className="text-base font-extrabold text-fg">{title}</DialogTitle>
            {dismissable && <IconButton icon={X} label="Закрыть" size="sm" onClick={onClose} />}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">{children}</div>

          {footer && (
            <div className="flex shrink-0 justify-end gap-2 border-t border-line px-5 py-3 max-sm:pb-[calc(0.75rem+var(--safe-bottom))]">
              {footer}
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
